import React, { useState, useRef, useEffect } from "react";
import PageHeader from "../../components/pageHeader";
import LiquidityCardLp from "../../components/liquidity/cardLP";
import config from "../../config";
import { LiquidityPool } from "../../interfaces/token";
import { useEthereum } from "../../contexts/etherruemContext";
import BigNumber from "bignumber.js";
import { lpType } from "../../constants";
import { useTokenHelpers } from "../../hooks/useTokenHelpers";
import useLiquidity from "../../hooks/useLiquidityHelpers";
import RemoveModal from "../../components/liquidity/removeModal";
import { useUnifiedWallet } from "../../providers/UnifiedWalletProvider";
import { useMainConstant } from "../../contexts/mainContext";
import { useNotification } from "../../contexts/notificationContext";
import Web3 from "web3";

export interface lpExtra extends LiquidityPool {
  lpBalance: number;
  token0Amount: BigNumber;
  token1Amount: BigNumber;
  percentOfPoolShare: BigNumber;
  isRemovingStatus: boolean;
  id: string;
  isAllowance: boolean;
}

const LiquidityPools: React.FC = () => {
  // const [joinCounter, setJoinCounter] = useState<number>(0);
  // const [lpTokens, setLpTokens] = useState<LpToken[]>([]);
  // const [isLoadFinish, setIsLoadFinish] = useState<boolean>(true);

  const [configLPList, setConfigLPList] = useState<LiquidityPool[]>([]);
  const [lpTokens, setLpTokens] = useState<lpExtra[]>([]);
  const [joinCounter, setJoinCounter] = useState<number>(0);
  const [isLoadFinish, setIsLoadFinish] = useState<boolean>(false);
  const [triggerLp, setTriggerLp] = useState<boolean>(false);

  const ethereum = useEthereum();
  // const wallet = useWallet();
  const mainConstant = useMainConstant();
  const { setNotify } = useNotification();

  const { getAllowance } = useTokenHelpers();
  const {
    getToken0AndToken1InLp,
    getTotalPoolShare,
    getReserveInPool,
    isReady,
  } = useLiquidity();
  const { walletApi } = useUnifiedWallet();

  const removeLPModalRef = useRef<{ open: (lp: lpExtra) => void }>(null);

  const removeButtonClick = (lp: lpExtra) => {
    if (removeLPModalRef.current) {
      removeLPModalRef.current.open(lp);
    }
  };

  const addToken = (lp: lpExtra) => {
    console.log("Add token clicked for", lp);
  };

  const handleConnectWallet = () => {
    // Open wallet connection modal
    mainConstant.setIsShowConnectModal(true);
  };

  // const onClickDocument = (id: string) => {
  //   console.log("Document clicked for", id);
  // };

  const onConfirmRemove = (
    lpTokenID: string,
    pendingStatus: boolean,
    state = "remove"
  ) => {
    setLpTokens((lpTokens) =>
      lpTokens.map((lpToken) => {
        if (lpToken.id === lpTokenID) {
          lpToken.isRemovingStatus = pendingStatus;
        }
        return lpToken;
      })
    );
    if (!pendingStatus && state === "remove") {
      // const ref = `liquidityExpansionPanel${lpTokenID}`
      getAllLpInfo();
      // this.$refs[ref][0].handleExpand(true)
    }
  };

  const getAllLpInfo = async () => {
    // if (!ethereum.contracts || !ethereum.web3 || !walletApi.account.current) {
    //   setTimeout(() => {
    //     getAllLpInfo();
    //   }, 2000);
    //   return;
    // }

    let zeroLP: any[] = [];
    let ownLP: any[] = [];

    try {
      await Promise.all(
        configLPList
          .filter((lp) => {
            if (lp.isHidden) {
              return false; // skip
            }
            if (!lp.address || lp.address === "") {
              return false; // skip if address is undefined, null, or empty
            }
            return true;
          })
          .map(async (element) => {
            try {
              const lp = await getLpInfo(element);
              if (lp?.lpBalance && Number(lp?.lpBalance.toFixed(12)) > 0) {
                ownLP.push(lp);
              } else if (lp) zeroLP.push(lp);
            } catch (error: any) {
              // Handle errors from getLpInfo
              if (
                error?.message?.includes("Invalid value") ||
                error?.message?.includes("can not parse as number")
              ) {
                // Error already handled in getLpInfo with toast, just skip this LP
                console.warn("Skipping LP due to chain error:", element.code);
                return;
              }
              // Re-throw other errors
              throw error;
            }
          })
      );
    } catch (error: any) {
      // Handle any unexpected errors
      if (
        error?.message?.includes("Invalid value") ||
        error?.message?.includes("can not parse as number")
      ) {
        setNotify({
          status: "error",
          fullUrl: "NOLINK",
          txHash: "undefined",
          errorReason:
            "Unexpected response from chain, please try again in 30 minutes",
          duration: 10000,
          chainType: walletApi.chain?.chainType,
        });
      } else {
        console.error("Error in getAllLpInfo:", error);
      }
    }

    ownLP = sortLP(ownLP);
    zeroLP = sortLP(zeroLP);

    setLpTokens(JSON.parse(JSON.stringify(ownLP.concat(zeroLP))));

    setJoinCounter(ownLP.length);

    setIsLoadFinish(true);
  };

  const sortLP = (array: any[]) => {
    return array.sort((a, b) => {
      return a.sortOrder - b.sortOrder;
    });
  };

  const getLpInfo = async (lp: LiquidityPool) => {
    // Allow viewing pools without wallet (will show 0 balances)
    // if (!walletApi.hasAddress) return;
    const tokenSymbol = lp.code.replace("/", "_");
    const walletCurrent = walletApi.account.current || "";
    const poolInfo = walletApi.hasAddress
      ? await getTotalPoolShare(tokenSymbol, walletCurrent)
      : { lpBalance: "0", totalSupply: "1" }; // Default values when no wallet
    if (!poolInfo) return;
    // if poolInfo is {}, return
    if (Object.keys(poolInfo).length === 0 && poolInfo.constructor === Object) {
      return;
    }

    let lpBalance: string;
    try {
      if (!poolInfo.lpBalance || poolInfo.lpBalance === undefined) {
        lpBalance = "0";
      } else {
        lpBalance = Web3.utils.fromWei(poolInfo.lpBalance as string, "ether");
      }
    } catch (error: any) {
      // Handle InvalidNumberError from web3-utils
      if (
        error?.message?.includes("Invalid value") ||
        error?.message?.includes("can not parse as number")
      ) {
        setNotify({
          status: "error",
          fullUrl: "NOLINK",
          txHash: "undefined",
          errorReason:
            "Unexpected response from chain, please try again in 30 minutes",
          duration: 10000,
          chainType: walletApi.chain?.chainType,
        });
        return; // Return early to prevent further processing
      }
      throw error; // Re-throw if it's a different error
    }
    let token0Amount = BigNumber("0");
    let token1Amount = BigNumber("0");
    let percentOfPoolShare = BigNumber("0");
    const isRemovingStatus = false;
    const id = tokenSymbol;
    let isAllowance = false;

    const totalSupply = BigNumber(poolInfo.totalSupply as string);
    if (parseFloat(lpBalance) > 0) {
      const balance = BigNumber(poolInfo.lpBalance as string);
      percentOfPoolShare = balance.div(totalSupply).multipliedBy(100);

      // TODO getReserve need to change call with new method -- DMM getTradeInfo
      const tokenInPool = await getReserveInPool(tokenSymbol);
      // Handle different reserve data formats
      let reserve0 = BigNumber("0");
      let reserve1 = BigNumber("0");

      if (tokenInPool) {
        // Try different possible formats for reserve data
        if ((tokenInPool as any)._reserve0 !== undefined) {
          // Format: {_reserve0, _reserve1, _blockTimestampLast}
          reserve0 = BigNumber(
            Web3.utils.fromWei(
              (tokenInPool as any)._reserve0 as string,
              "ether"
            )
          );
          reserve1 = BigNumber(
            Web3.utils.fromWei(
              (tokenInPool as any)._reserve1 as string,
              "ether"
            )
          );
        } else if (Array.isArray(tokenInPool) && tokenInPool.length >= 2) {
          // Format: [reserve0, reserve1, timestamp]
          reserve0 = BigNumber(
            Web3.utils.fromWei(tokenInPool[0].toString(), "ether")
          );
          reserve1 = BigNumber(
            Web3.utils.fromWei(tokenInPool[1].toString(), "ether")
          );
        } else if (
          (tokenInPool as any)[0] !== undefined &&
          (tokenInPool as any)[1] !== undefined
        ) {
          // Format: {0: reserve0, 1: reserve1, 2: timestamp}
          reserve0 = BigNumber(
            Web3.utils.fromWei((tokenInPool as any)[0].toString(), "ether")
          );
          reserve1 = BigNumber(
            Web3.utils.fromWei((tokenInPool as any)[1].toString(), "ether")
          );
        }
      }

      // check swap token0 and token1
      const tokens = await getToken0AndToken1InLp(tokenSymbol);
      if (
        lp.token0.address?.toUpperCase() ===
        tokens.token1?.address?.toUpperCase()
      ) {
        // swap 0<->1
        const temp = reserve0;
        reserve0 = reserve1;
        reserve1 = temp;
      }

      // Ensure we have valid reserves before calculating token amounts
      if (reserve0.isZero() && reserve1.isZero()) {
        console.warn("Both reserves are zero for", tokenSymbol);
      }

      token0Amount = BigNumber(percentOfPoolShare)
        .multipliedBy(reserve0)
        .div(100);

      token1Amount = BigNumber(percentOfPoolShare)
        .multipliedBy(reserve1)
        .div(100);

      // Check if ethereum contracts are available before accessing router addresses
      // Only check allowance if wallet is connected
      if (walletApi.hasAddress && ethereum.contracts) {
        let routerAddress = ethereum.contracts.ammRouter?._address;
        if (lp.lpType === lpType.DMM_TYPE) {
          routerAddress = ethereum.contracts.dmmRouter?._address;
        }

        if (routerAddress) {
          // For LP tokens, we need to check allowance using the LP token's contract address
          const lpTokenAddress = lp.address;
          if (lpTokenAddress) {
            const allowance = await getAllowance(
              tokenSymbol,
              walletCurrent,
              routerAddress,
              lpTokenAddress
            );
            isAllowance = !!(
              allowance &&
              BigNumber(allowance).gte(poolInfo.lpBalance as string)
            );
          }
        }
      }
    }
    const lpResult: lpExtra = {
      ...lp,
      lpBalance: parseFloat(BigNumber(lpBalance).toPrecision(16)),
      token0Amount,
      token1Amount,
      percentOfPoolShare,
      isRemovingStatus,
      id,
      isAllowance,
    };

    return lpResult;
  };

  // useEffect(() => {
  // Clear all notifications
  // dispatch(clearAllNotifications());

  // setConfigLPList(
  //   Object.keys(config.lp).map((key) => {
  //     return config.lp[key];
  //   })
  // );

  // setConfigDMMLPList(
  //   Object.keys(config.dmmLP).map((key) => {
  //     return config.dmmLP[key];
  //   })
  // );

  // setConfigLPList(
  //   Object.keys(config.lp)
  //     .map((key) => {
  //       return config.lp[key];
  //     })
  //     .concat(
  //       Object.keys(config.dmmLP).map((key) => {
  //         return config.dmmLP[key];
  //       })
  //     )
  // );

  // getAllLpInfo();

  // const handleWalletUpdated = () => {
  // getAllLpInfo();
  // if (removeLPModalRef.current) {
  //   (removeLPModalRef.current as any).close(); // Close modal
  // }
  // };

  // window.addEventListener('wallet-updated', handleWalletUpdated);

  // return () => {
  //   window.removeEventListener('wallet-updated', handleWalletUpdated);
  // };
  // }, []);

  useEffect(() => {
    // Load pool list even without wallet to allow browsing
    if (isReady) {
      setConfigLPList(
        Object.keys(config.lp)
          .filter((key) => {
            const lp = config.lp[key];
            return lp?.address && !lp.isHidden;
          })
          .map((key) => {
            return config.lp[key];
          })
          .concat(
            Object.keys(config.dmmLP)
              .filter((key) => {
                const lp = config.dmmLP[key];
                return lp?.address && !lp.isHidden;
              })
              .map((key) => {
                return config.dmmLP[key];
              })
          )
      );
    } else {
      setConfigLPList([]);
    }
  }, [walletApi.hasAddress, walletApi.isConnected, isReady]);

  useEffect(() => {
    // Load pool info even without wallet (will show pools with 0 balance)
    if (configLPList.length > 0 && isReady) {
      getAllLpInfo();
    } else {
      setLpTokens([]);
      setJoinCounter(0);
      setIsLoadFinish(false);
    }
  }, [configLPList, triggerLp]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTriggerLp(!triggerLp);
    }, 3000);

    return () => clearInterval(intervalId);
  }, [triggerLp]);

  // useEffect(() => {
  //   console.log("setConfigLPList configLPList", configLPList);
  //   console.log("setConfigLPList configDMMLPList", configDMMLPList);
  //   setConfigLPList(configLPList.concat(configDMMLPList));
  // }, [configLPList, configDMMLPList]);

  return (
    <div className="md:container p-0">
      <div className="xl:ml-32 md:flex md:flex-col xl:max-w-[867px]">
        <PageHeader
          title="Liquidity Pools"
          subTitle={
            walletApi.hasAddress
              ? `You have joined ${joinCounter} from ${lpTokens.length} Liquidity Pools`
              : `${lpTokens.length} Liquidity Pools available. Connect wallet to view your positions.`
          }
        />
        <div className="md:mb-2 px-4 md:px-0 md:container md:mx-auto items-center justify-center">
          {isLoadFinish && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5 animate__animated animate__fadeInUp">
              {lpTokens.map((value, index) => (
                <div key={index} className="bg-cover border-none">
                  <LiquidityCardLp
                    // id={value.sortOrder}
                    // ref={`liquidityExpansionPanel${value.id}`}
                    lpToken={value}
                    isProcessing={value.isRemovingStatus}
                    onClickRemove={removeButtonClick}
                    onClickAddToken={addToken}
                    hasWallet={walletApi.hasAddress}
                    onConnectWallet={handleConnectWallet}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <RemoveModal ref={removeLPModalRef} onConfirmRemove={onConfirmRemove} />
    </div>
  );
};

export default LiquidityPools;
